import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Compass, X, Volume2, VolumeX } from "lucide-react";
import SoulOrb from "@/components/SoulOrb";
import { getTodaysIntention, clearTodaysIntention } from "@/lib/dailyIntention";
import { canSpeak, speak, stopSpeaking } from "@/lib/readAloud";

/**
 * TodaysIntentionCard — the Home page anchor.
 *
 * Displays today's chosen intention with a small orb beside it. Renders
 * nothing when no intention has been set (the Home page stays quiet).
 * Subscribes to `daily-intention-changed` so it updates the moment a
 * member picks a new intention on the Mind → Daily Intentions tab.
 */
export default function TodaysIntentionCard() {
  const [pick, setPick] = useState(() => getTodaysIntention());
  const [pulseSeed, setPulseSeed] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    const refresh = () => setPick(getTodaysIntention());
    window.addEventListener("daily-intention-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("daily-intention-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Soft hello pulse whenever the selection changes; also stop any playback.
  useEffect(() => {
    if (pick) setPulseSeed((s) => s + 1);
    stopSpeaking();
    setSpeaking(false);
  }, [pick?.text]);

  if (!pick) return null;

  const release = () => {
    stopSpeaking();
    setSpeaking(false);
    clearTodaysIntention();
    setPick(null);
    toast("Released.");
  };

  const toggleRead = () => {
    if (speaking) { stopSpeaking(); setSpeaking(false); return; }
    if (!canSpeak()) { toast.error("Read aloud isn't available on this browser."); return; }
    const ok = speak(pick.text, { rate: 0.85, onEnd: () => setSpeaking(false) });
    if (ok) setSpeaking(true);
  };

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--terracotta))]/8 via-[hsl(var(--paper))] to-[hsl(var(--sand))]/30 p-6 sm:p-7 reveal"
      data-testid="home-todays-intention"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-[0.09]"
        style={{ background: "radial-gradient(circle, hsl(var(--terracotta)) 0, transparent 65%)" }}
      />

      <div className="relative flex items-start gap-4 sm:gap-6">
        <SoulOrb
          size="md"
          mode="aligned"
          pulseSeed={pulseSeed}
          glow={0.75}
          ariaLabel="Soul orb — carrying today's intention"
          testid="home-todays-intention-orb"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--terracotta))]">
            <Compass size={11} />
            <span data-testid="home-todays-intention-eyebrow">Today&apos;s Intention</span>
            {pick.category_title && (
              <span className="text-[hsl(var(--muted-foreground))] normal-case tracking-normal">· {pick.category_title}</span>
            )}
          </div>
          <p
            className="mt-2 font-display italic text-xl sm:text-2xl leading-snug text-[hsl(var(--fg))]"
            data-testid="home-todays-intention-text"
          >
            {pick.text}
          </p>
          <p
            className="mt-2 text-[12px] italic text-[hsl(var(--muted-foreground))]"
            data-testid="home-todays-intention-subcopy"
          >
            Return to this today.
          </p>
          <div className="mt-3 flex items-center justify-end gap-3">
            {canSpeak() && (
              <button
                type="button"
                onClick={toggleRead}
                aria-label={speaking ? "Stop reading" : "Read aloud"}
                aria-pressed={speaking}
                data-testid="home-todays-intention-speaker"
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] tab-link ${
                  speaking
                    ? "text-[hsl(var(--terracotta))]"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))]"
                }`}
              >
                {speaking ? <VolumeX size={11} /> : <Volume2 size={11} />}
                {speaking ? "Stop" : "Read aloud"}
              </button>
            )}
            <button
              type="button"
              onClick={release}
              aria-label="Release today's intention"
              data-testid="home-todays-intention-release"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] tab-link"
            >
              <X size={11} />
              Release
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
